import { io, Socket } from 'socket.io-client';
import Storage from './Storage';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://72.61.255.221:8012';

type EventCallback = (data: any) => void;

/**
 * SocketService - Manages WebSocket connections using Socket.IO
 * Provides singleton instance for real-time communication
 */
class SocketService {
    private socket: Socket | null = null;
    private listeners: Map<string, Set<EventCallback>> = new Map();

    /**
     * Connect to the socket server
     * Requires authentication token from storage
     */
    async connect(): Promise<void> {
        if (this.socket?.connected) {
            console.log('[Socket] Already connected');
            return;
        }

        // Get token from localStorage (use the same key as apiClient)
        const token = await Storage.get('kitchenAuthToken');
        if (!token) {
            console.warn('[Socket] No authentication token found');
            return;
        }

        this.socket = io(SOCKET_URL, {
            transports: ['websocket'],
            query: { token },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
        });

        this.socket.on('connect', () => {
            console.log('[Socket] Connected:', this.socket?.id);
        });

        this.socket.on('disconnect', (reason: any) => {
            console.log('[Socket] Disconnected:', reason);
        });

        this.socket.on('connect_error', (err: any) => {
            console.error('[Socket] Connection error:', err.message);
        });

        this.socket.on('error', (err: any) => {
            console.error('[Socket] Error:', err);
        });

        // Re-attach any pending listeners
        this.listeners.forEach((callbacks, event) => {
            callbacks.forEach((cb) => {
                this.socket?.on(event, cb);
            });
        });
    }

    /**
     * Disconnect from the socket server
     */
    disconnect(): void {
        if (this.socket) {
            console.log('[Socket] Disconnecting...');
            this.socket.disconnect();
            this.socket = null;
        }
    }

    /**
     * Register an event listener
     * @param event - Event name to listen for
     * @param callback - Callback function to execute when event is received
     */
    on(event: string, callback: EventCallback): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
        this.socket?.on(event, callback);
    }

    /**
     * Remove an event listener
     * @param event - Event name
     * @param callback - Callback function to remove
     */
    off(event: string, callback: EventCallback): void {
        this.listeners.get(event)?.delete(callback);
        this.socket?.off(event, callback);
    }

    /**
     * Emit an event to the server
     * @param event - Event name
     * @param data - Data to send with the event
     */
    emit(event: string, data?: any): void {
        if (!this.socket) {
            console.warn('[Socket] Cannot emit - not connected');
            return;
        }
        this.socket.emit(event, data);
    }

    /**
     * Check if socket is connected
     * @returns True if connected, false otherwise
     */
    isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    /**
     * Get the socket instance (use with caution)
     * @returns Socket instance or null
     */
    getSocket(): Socket | null {
        return this.socket;
    }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
